import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronRight } from 'lucide-react';
import { cx, useIsPhone } from '@/ui/primitives';
import { useMarkRead, useNotifications, useUnreadCount, type NotificationView } from './api';
import { relativeTime, severityVisual } from './format';

function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="bg-danger absolute top-2 right-2 flex size-4 items-center justify-center rounded-full text-[10px] font-semibold text-white sm:top-1 sm:right-1">
      {count > 9 ? '9+' : count}
    </span>
  );
}

/**
 * The bell in the header.
 *
 * On a phone it is a link, not a menu: a floating 320px panel anchored to the
 * right edge of a 360px viewport is a panel with its left side off the screen,
 * and the rows inside it were too small to hit reliably anyway. Tapping it goes
 * to /bildirimler, which is the same list with room to breathe — and the same
 * place a push notification opens, so there is one notification screen on a
 * phone rather than two that disagree.
 *
 * On a wide screen the dropdown is right and stays.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const isPhone = useIsPhone();

  const unread = useUnreadCount();
  // The dropdown deliberately reads the UNFILTERED first page and nothing else:
  // a peek at the newest few. Filtering and paging live on /bildirimler, which
  // is where a 320px panel could not have carried them anyway.
  const list = useNotifications(open && !isPhone);
  const items = list.data?.pages[0] ?? [];
  const markRead = useMarkRead();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // The dropdown must not survive a shrink to phone width — it would be left
  // hanging off the right edge with no way to close it.
  useEffect(() => {
    if (isPhone) setOpen(false);
  }, [isPhone]);

  const count = unread.data?.count ?? 0;
  const label = count > 0 ? `${count} okunmamış bildirim` : 'Bildirimler';

  const openItem = (n: NotificationView) => {
    if (!n.readAt) markRead.mutate(n.id);
    setOpen(false);
    if (n.actionUrl) navigate(n.actionUrl);
  };

  if (isPhone) {
    return (
      <button
        type="button"
        onClick={() => navigate('/bildirimler')}
        aria-label={label}
        className="text-fg-muted hover:text-fg hover:bg-surface-muted active:bg-surface-muted relative inline-flex size-11 shrink-0 touch-manipulation items-center justify-center rounded-lg transition"
      >
        <Bell size={20} />
        <UnreadBadge count={count} />
      </button>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        aria-expanded={open}
        className="text-fg-muted hover:text-fg hover:bg-surface-muted relative inline-flex size-9 items-center justify-center rounded-lg transition"
      >
        <Bell size={18} />
        <UnreadBadge count={count} />
      </button>

      {open && (
        <div className="border-border bg-surface absolute right-0 z-30 mt-1 w-80 rounded-xl border shadow-lg">
          <div className="border-border flex items-center justify-between border-b px-3 py-2">
            <span className="text-fg text-sm font-medium">Bildirimler</span>
            {count > 0 && (
              <button
                type="button"
                onClick={() => markRead.mutate(undefined)}
                className="text-fg-muted hover:text-fg text-xs"
              >
                Tümünü okundu işaretle
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {list.isPending && (
              <p className="text-fg-muted px-3 py-6 text-center text-sm">Yükleniyor…</p>
            )}
            {list.isSuccess && items.length === 0 && (
              <p className="text-fg-muted px-3 py-6 text-center text-sm">Bildiriminiz yok.</p>
            )}
            {items.map((n) => {
              const { Icon, className } = severityVisual(n.severity);
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openItem(n)}
                  className={cx(
                    'border-border hover:bg-surface-muted w-full border-b px-3 py-2.5 text-left transition last:border-b-0',
                    !n.readAt && 'bg-primary/5',
                  )}
                >
                  <div className="flex items-start gap-2">
                    <Icon size={15} className={cx('mt-0.5 shrink-0', className)} />
                    <div className="min-w-0">
                      <p className="text-fg text-sm font-medium">{n.title}</p>
                      <p className="text-fg-muted mt-0.5 text-xs whitespace-pre-line">{n.body}</p>
                      <p className="text-fg-muted mt-1 text-[11px]">{relativeTime(n.createdAt)}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/bildirimler');
            }}
            className="border-border text-fg-muted hover:text-fg hover:bg-surface-muted flex w-full items-center justify-center gap-1 border-t px-3 py-2 text-xs transition"
          >
            Tümünü gör <ChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}
